// Synthesized Web Audio API Sound Effects, High-Fidelity Quick!Bible Instrumental Soundtracks & Web Speech API Narration

export type BackgroundMusicMood =
  | 'auto'
  | 'relaxed'
  | 'inspirational'
  | 'cinematic'
  | 'competitive';

export type ActiveMusicTrackMood =
  | 'auto'
  | 'relaxed'
  | 'inspirational'
  | 'cinematic'
  | 'competitive';

export const BACKGROUND_MUSIC_TRACKS: Record<
  BackgroundMusicMood,
  { path: string; label: string; desc: string }
> = {
  auto: {
    path: '/audio/auto-adaptive.mp3',
    label: 'Auto (Adaptive)',
    desc: 'Default adaptive soundtrack',
  },
  relaxed: {
    path: '/audio/relaxed-sanctuary.mp3',
    label: 'Relaxed Sanctuary',
    desc: 'Peaceful Bible study atmosphere',
  },
  inspirational: {
    path: '/audio/inspirational.mp3',
    label: 'Inspirational',
    desc: 'Motivational and uplifting atmosphere',
  },
  cinematic: {
    path: '/audio/cinematic.mp3',
    label: 'Cinematic',
    desc: 'Deep orchestral challenge atmosphere',
  },
  competitive: {
    path: '/audio/competitive-focus.mp3',
    label: 'Competitive Focus',
    desc: 'Rhythmic competitive gaming atmosphere',
  },
};

const AUDIO_PREFS_STORAGE_KEY = 'qb_audio_prefs_v2';

export interface StoredAudioPreferences {
  bgMusicEnabled: boolean;
  bgMusicMuted: boolean;
  bgMusicVolume: number; // 0..100
  bgMusicMood: BackgroundMusicMood;
  sfxEnabled: boolean;
  sfxVolume: number; // 0..100
  narrationEnabled: boolean;
}

class SoundAndSpeechEngine {
  private ctx: AudioContext | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private currentTrackUrl: string | null = null;
  private isPlayingBg = false;
  private crossfadeTimer: number | null = null;
  private wasPausedByVisibility = false;
  private userInteractionListenerAttached = false;
  private contextMoodHint: ActiveMusicTrackMood = 'auto';
  private isNarrationSpeaking = false;
  private listeners = new Set<() => void>();

  // Default OFF for first-time visitors (Section 12 requirement)
  public sfxEnabled = true;
  public sfxVolume = 0.8;
  public bgMusicEnabled = false;
  public bgMusicMuted = false;
  public bgMusicVolume = 0.35;
  public bgMusicMood: BackgroundMusicMood = 'auto';
  public isInQuizMode = false;
  public narrationEnabled = true;
  public narrateChoices = true;
  public narrateInstructions = true;
  public narrationRate = 1.0;

  constructor() {
    this.loadFromLocalStorage();
    this.setupVisibilityListener();
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    for (const fn of this.listeners) {
      try {
        fn();
      } catch {
        // Ignore listener error
      }
    }
  }

  public loadFromLocalStorage() {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(AUDIO_PREFS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<StoredAudioPreferences>;
      if (typeof parsed.bgMusicEnabled === 'boolean') {
        this.bgMusicEnabled = parsed.bgMusicEnabled;
      }
      if (typeof parsed.bgMusicMuted === 'boolean') {
        this.bgMusicMuted = parsed.bgMusicMuted;
      }
      if (typeof parsed.bgMusicVolume === 'number') {
        this.bgMusicVolume = Math.max(0, Math.min(1, parsed.bgMusicVolume / 100));
      }
      if (
        parsed.bgMusicMood &&
        ['auto', 'relaxed', 'inspirational', 'cinematic', 'competitive'].includes(
          parsed.bgMusicMood
        )
      ) {
        this.bgMusicMood = parsed.bgMusicMood;
      }
      if (typeof parsed.sfxEnabled === 'boolean') {
        this.sfxEnabled = parsed.sfxEnabled;
      }
      if (typeof parsed.sfxVolume === 'number') {
        this.sfxVolume = Math.max(0, Math.min(1, parsed.sfxVolume / 100));
      }
      if (typeof parsed.narrationEnabled === 'boolean') {
        this.narrationEnabled = parsed.narrationEnabled;
      }
    } catch {
      // Ignore storage errors
    }
  }

  public saveToLocalStorage(userUid?: string) {
    if (typeof window === 'undefined') return;
    const prefs: StoredAudioPreferences = {
      bgMusicEnabled: this.bgMusicEnabled,
      bgMusicMuted: this.bgMusicMuted,
      bgMusicVolume: Math.round(this.bgMusicVolume * 100),
      bgMusicMood: this.bgMusicMood,
      sfxEnabled: this.sfxEnabled,
      sfxVolume: Math.round(this.sfxVolume * 100),
      narrationEnabled: this.narrationEnabled,
    };
    try {
      window.localStorage.setItem(AUDIO_PREFS_STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Ignore
    }
    this.notifyListeners();

    if (userUid && !userUid.startsWith('guest')) {
      fetch('/api/audio/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userUid, ...prefs }),
      }).catch(() => {});
    }
  }

  public applyServerPreferences(prefs: Partial<StoredAudioPreferences>) {
    if (typeof prefs.bgMusicEnabled === 'boolean') {
      this.bgMusicEnabled = prefs.bgMusicEnabled;
    }
    if (typeof prefs.bgMusicMuted === 'boolean') {
      this.bgMusicMuted = prefs.bgMusicMuted;
    }
    if (typeof prefs.bgMusicVolume === 'number') {
      this.bgMusicVolume = Math.max(0, Math.min(1, prefs.bgMusicVolume / 100));
    }
    if (prefs.bgMusicMood) {
      this.bgMusicMood = prefs.bgMusicMood;
    }
    if (typeof prefs.sfxEnabled === 'boolean') {
      this.sfxEnabled = prefs.sfxEnabled;
    }
    if (typeof prefs.narrationEnabled === 'boolean') {
      this.narrationEnabled = prefs.narrationEnabled;
    }
    this.updateBgGainLevel();
    this.notifyListeners();
  }

  private getContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AudioCtx =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  public getTrackUrl(mood: BackgroundMusicMood): string {
    return BACKGROUND_MUSIC_TRACKS[mood]?.path || BACKGROUND_MUSIC_TRACKS.auto.path;
  }

  public resolveActiveMood(): ActiveMusicTrackMood {
    if (this.bgMusicMood !== 'auto') {
      return this.bgMusicMood;
    }
    return this.contextMoodHint || 'auto';
  }

  /**
   * Automatically adapts the musical atmosphere to the active game mode & difficulty
   * when bgMusicMood is set to 'auto'.
   */
  public setGameplayContextMood(params: {
    gameMode?: string;
    difficulty?: string;
    level?: number;
  }) {
    const mode = String(params.gameMode || '').toLowerCase();
    const diff = String(params.difficulty || '').toLowerCase();
    const lvl = Number(params.level) || 1;

    let target: ActiveMusicTrackMood = 'auto';
    if (mode === 'ranked') {
      target = 'competitive';
    } else if (
      diff.includes('very difficult') ||
      diff.includes('difficult') ||
      diff.includes('expert') ||
      lvl >= 11
    ) {
      target = 'cinematic';
    } else if (
      mode === 'challenge' ||
      diff.includes('hard') ||
      diff.includes('medium') ||
      diff.includes('intermediate') ||
      lvl >= 4
    ) {
      target = 'inspirational';
    } else {
      target = 'auto';
    }

    this.contextMoodHint = target;
    // Internal navigation between modes seamlessly continues without restarting
    this.notifyListeners();
  }

  public setQuizMode(inQuiz: boolean) {
    this.isInQuizMode = inQuiz;
    if (inQuiz && this.bgMusicEnabled && !this.bgMusicMuted) {
      this.startBackgroundMusic();
    } else {
      this.stopBackgroundMusic();
    }
  }

  public setBgMusicEnabled(enabled: boolean, userUid?: string) {
    this.bgMusicEnabled = enabled;
    if (enabled && this.isInQuizMode) {
      this.bgMusicMuted = false;
      this.startBackgroundMusic();
    } else {
      this.stopBackgroundMusic();
    }
    this.saveToLocalStorage(userUid);
  }

  public setBgMusicMuted(muted: boolean, userUid?: string) {
    this.bgMusicMuted = muted;
    this.updateAudioVolume();
    this.saveToLocalStorage(userUid);
  }

  public setBgMusicVolume(volume0To1: number, userUid?: string) {
    this.bgMusicVolume = Math.max(0, Math.min(1, volume0To1));
    if (this.bgMusicVolume > 0 && this.bgMusicMuted) {
      this.bgMusicMuted = false;
    }
    this.updateAudioVolume();
    this.saveToLocalStorage(userUid);
  }

  public setBgMusicMood(mood: BackgroundMusicMood, userUid?: string) {
    if (this.bgMusicMood === mood && this.isPlayingBg) {
      this.saveToLocalStorage(userUid);
      return;
    }
    this.bgMusicMood = mood;
    if (this.bgMusicEnabled) {
      const targetUrl = this.getTrackUrl(mood);
      this.switchTrackWithCrossfade(targetUrl);
    }
    this.saveToLocalStorage(userUid);
  }

  public getEffectiveVolume(): number {
    if (!this.bgMusicEnabled || this.bgMusicMuted) return 0;
    // Duck background music while question narration is speaking so voice remains clear
    const duckFactor = this.isNarrationSpeaking ? 0.35 : 1.0;
    return Math.max(0, Math.min(1, this.bgMusicVolume * duckFactor));
  }

  private getEffectiveGainValue(): number {
    return this.getEffectiveVolume();
  }

  public updateAudioVolume() {
    if (this.currentAudio && this.crossfadeTimer === null) {
      try {
        this.currentAudio.volume = this.getEffectiveVolume();
      } catch {
        // Ignore volume clamp error
      }
    }
  }

  private updateBgGainLevel() {
    this.updateAudioVolume();
  }

  private setupVisibilityListener() {
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.currentAudio && !this.currentAudio.paused) {
          this.wasPausedByVisibility = true;
          this.currentAudio.pause();
          this.isPlayingBg = false;
          this.notifyListeners();
        }
      } else {
        if (this.wasPausedByVisibility && this.bgMusicEnabled && !this.bgMusicMuted) {
          this.wasPausedByVisibility = false;
          if (this.currentAudio) {
            this.currentAudio.volume = this.getEffectiveVolume();
            const p = this.currentAudio.play();
            if (p !== undefined) {
              p.then(() => {
                this.isPlayingBg = true;
                this.notifyListeners();
              }).catch(() => {
                this.setupAutoplayUnlock();
              });
            }
          } else {
            this.startBackgroundMusic();
          }
        }
      }
    });
  }

  private setupAutoplayUnlock() {
    if (typeof window === 'undefined' || this.userInteractionListenerAttached) return;
    this.userInteractionListenerAttached = true;

    const unlock = () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      this.userInteractionListenerAttached = false;

      if (this.bgMusicEnabled && (!this.currentAudio || this.currentAudio.paused)) {
        this.startBackgroundMusic();
      }
    };

    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown', unlock, { once: true, passive: true });
  }

  private restartBackgroundMusicWithCurrentMood() {
    if (this.bgMusicEnabled) {
      this.startBackgroundMusic();
    }
  }

  public playCorrect(_key?: string) {
    if (!this.sfxEnabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + i * 0.07);

      gain.gain.setValueAtTime(0.001, now + i * 0.07);
      gain.gain.exponentialRampToValueAtTime(
        Math.max(0.02, 0.22 * this.sfxVolume),
        now + i * 0.07 + 0.02
      );
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.07 + 0.28);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.07);
      osc.stop(now + i * 0.07 + 0.3);
    });
  }

  public playWrong(_key?: string) {
    if (!this.sfxEnabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [311.13, 277.18]; // D#4, C#4
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, now + i * 0.14);

      gain.gain.setValueAtTime(0.001, now + i * 0.14);
      gain.gain.exponentialRampToValueAtTime(
        Math.max(0.02, 0.16 * this.sfxVolume),
        now + i * 0.14 + 0.02
      );
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.14 + 0.32);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.14);
      osc.stop(now + i * 0.14 + 0.35);
    });
  }

  public playTick(urgent = false) {
    if (!this.sfxEnabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(urgent ? 880 : 620, now);

    gain.gain.setValueAtTime(
      Math.max(0.01, (urgent ? 0.12 : 0.05) * this.sfxVolume),
      now
    );
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.07);
  }

  public playFanfare() {
    if (!this.sfxEnabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const chord = [440, 554.37, 659.25, 880]; // A Major
    chord.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + idx * 0.09);

      gain.gain.setValueAtTime(0.001, now + idx * 0.09);
      gain.gain.exponentialRampToValueAtTime(
        Math.max(0.02, 0.2 * this.sfxVolume),
        now + idx * 0.09 + 0.04
      );
      gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.09 + 0.7);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + idx * 0.09);
      osc.stop(now + idx * 0.09 + 0.75);
    });
  }

  /**
   * High-Fidelity Quick!Bible Instrumental Soundtracks:
   * - 'auto': Auto (Adaptive) -> /audio/auto-adaptive.mp3
   * - 'relaxed': Relaxed Sanctuary -> /audio/relaxed-sanctuary.mp3
   * - 'inspirational': Inspirational -> /audio/inspirational.mp3
   * - 'cinematic': Cinematic -> /audio/cinematic.mp3
   * - 'competitive': Competitive Focus -> /audio/competitive-focus.mp3
   */
  public startBackgroundMusic() {
    if (!this.bgMusicEnabled || !this.isInQuizMode) {
      this.stopBackgroundMusic();
      return;
    }

    const targetUrl = this.getTrackUrl(this.bgMusicMood);

    // If already playing the requested track seamlessly, maintain playback without interruption
    if (this.currentAudio && this.currentTrackUrl === targetUrl) {
      this.updateAudioVolume();
      if (this.currentAudio.paused) {
        const p = this.currentAudio.play();
        if (p !== undefined) {
          p.then(() => {
            this.isPlayingBg = true;
            this.notifyListeners();
          }).catch(() => {
            this.setupAutoplayUnlock();
          });
        }
      } else {
        this.isPlayingBg = true;
        this.notifyListeners();
      }
      return;
    }

    // Switch or start track with smooth crossfade
    this.switchTrackWithCrossfade(targetUrl);
  }

  private switchTrackWithCrossfade(targetUrl: string) {
    if (typeof window === 'undefined') return;

    if (this.crossfadeTimer !== null) {
      window.clearInterval(this.crossfadeTimer);
      this.crossfadeTimer = null;
    }

    const oldAudio = this.currentAudio;

    // If no previous audio or it was paused, start directly
    if (!oldAudio || oldAudio.paused) {
      this.startTrackDirectly(targetUrl);
      return;
    }

    // Create new audio element with metadata-only loading to prevent unnecessary downloads
    const nextAudio = new Audio();
    nextAudio.src = targetUrl;
    nextAudio.preload = 'metadata';
    nextAudio.loop = true;
    nextAudio.volume = 0;

    nextAudio.addEventListener('error', (e) => {
      console.warn('[Quick!Bible Audio] Error playing soundtrack:', targetUrl, e);
      this.isPlayingBg = false;
      this.notifyListeners();
    });

    const playPromise = nextAudio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.currentAudio = nextAudio;
          this.currentTrackUrl = targetUrl;
          this.isPlayingBg = true;
          this.notifyListeners();

          const crossfadeDurationMs = 400; // 300-500ms audio crossfade
          const intervalMs = 25;
          const totalSteps = Math.max(1, Math.floor(crossfadeDurationMs / intervalMs));
          let currentStep = 0;
          const initialOldVolume = oldAudio.volume;

          this.crossfadeTimer = window.setInterval(() => {
            currentStep++;
            const progress = Math.min(1, currentStep / totalSteps);
            const currentTargetVol = this.getEffectiveVolume();

            try {
              oldAudio.volume = Math.max(0, initialOldVolume * (1 - progress));
              nextAudio.volume = Math.min(1, currentTargetVol * progress);
            } catch {
              // Ignore volume clamp error
            }

            if (progress >= 1) {
              if (this.crossfadeTimer !== null) {
                window.clearInterval(this.crossfadeTimer);
                this.crossfadeTimer = null;
              }
              try {
                oldAudio.pause();
                oldAudio.src = '';
                oldAudio.load();
              } catch {
                // Ignore
              }
              try {
                nextAudio.volume = this.getEffectiveVolume();
              } catch {
                // Ignore
              }
            }
          }, intervalMs);
        })
        .catch((err) => {
          console.warn('[Quick!Bible Audio] Autoplay deferred until user interaction:', err);
          this.setupAutoplayUnlock();
        });
    }
  }

  private startTrackDirectly(targetUrl: string) {
    if (typeof window === 'undefined') return;

    if (this.crossfadeTimer !== null) {
      window.clearInterval(this.crossfadeTimer);
      this.crossfadeTimer = null;
    }

    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.src = '';
        this.currentAudio.load();
      } catch {
        // Ignore
      }
    }

    const audio = new Audio();
    audio.src = targetUrl;
    audio.preload = 'metadata';
    audio.loop = true;
    audio.volume = this.getEffectiveVolume();

    audio.addEventListener('error', (e) => {
      console.warn('[Quick!Bible Audio] Error loading soundtrack:', targetUrl, e);
      this.isPlayingBg = false;
      this.notifyListeners();
    });

    this.currentAudio = audio;
    this.currentTrackUrl = targetUrl;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.isPlayingBg = true;
          this.notifyListeners();
        })
        .catch((err) => {
          console.warn('[Quick!Bible Audio] Autoplay deferred until user interaction:', err);
          this.setupAutoplayUnlock();
        });
    }
  }

  public stopBackgroundMusic() {
    if (this.crossfadeTimer !== null) {
      window.clearInterval(this.crossfadeTimer);
      this.crossfadeTimer = null;
    }

    if (this.currentAudio) {
      const audioToStop = this.currentAudio;
      const startVol = audioToStop.volume;
      let step = 0;
      const fadeTimer = window.setInterval(() => {
        step++;
        const p = Math.min(1, step / 6);
        try {
          audioToStop.volume = Math.max(0, startVol * (1 - p));
        } catch {
          // Ignore
        }
        if (p >= 1) {
          window.clearInterval(fadeTimer);
          try {
            audioToStop.pause();
          } catch {
            // Ignore
          }
        }
      }, 25);
    }

    this.isPlayingBg = false;
    this.notifyListeners();
  }

  public getIsPlayingBg(): boolean {
    return this.isPlayingBg && !!this.currentAudio && !this.currentAudio.paused;
  }

  public getActiveResolvedMood(): ActiveMusicTrackMood {
    return this.resolveActiveMood();
  }

  public narrationVoice: string = 'female';
  public narrationSpeed: 'slow' | 'normal' | 'fast' | number = 'normal';

  public resetSessionSounds() {
    this.stopSpeaking();
  }

  public playClick() {
    if (!this.sfxEnabled) return;
    const ctx = this.getContext();
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(520, now);
      gain.gain.setValueAtTime(Math.max(0.01, 0.04 * this.sfxVolume), now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.035);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.04);
    } catch {
      // Ignore
    }
  }

  public toggleBackgroundMusic(enable?: boolean, volume?: number) {
    if (typeof volume === 'number') {
      this.setBgMusicVolume(volume);
    }
    const next = typeof enable === 'boolean' ? enable : !this.bgMusicEnabled;
    this.setBgMusicEnabled(next);
  }

  public isSpeaking(): boolean {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return false;
    }
    return (
      this.isNarrationSpeaking ||
      window.speechSynthesis.speaking ||
      window.speechSynthesis.pending
    );
  }

  public playVictoryFanfare(_key?: string) {
    this.playFanfare();
  }

  public stopNarration() {
    this.stopSpeaking();
  }

  public speakText(
    text: string,
    opts?: { language?: string; force?: boolean }
  ) {
    if ((!this.narrationEnabled && !opts?.force) || typeof window === 'undefined') {
      return;
    }
    if (!('speechSynthesis' in window)) return;
    try {
      this.stopSpeaking();
      const utterance = new SpeechSynthesisUtterance(text);
      const langMap: Record<string, string> = {
        en: 'en-US',
        tl: 'fil-PH',
        ceb: 'fil-PH',
        ko: 'ko-KR',
        'zh-CN': 'zh-CN',
        'zh-TW': 'zh-TW',
        es: 'es-ES',
      };
      utterance.lang = langMap[opts?.language || 'en'] || 'en-US';
      utterance.rate =
        this.narrationSpeed === 'slow'
          ? 0.85
          : this.narrationSpeed === 'fast'
          ? 1.15
          : this.narrationRate;
      utterance.onstart = () => {
        this.isNarrationSpeaking = true;
        this.updateBgGainLevel();
        this.notifyListeners();
      };
      utterance.onend = () => {
        this.isNarrationSpeaking = false;
        this.updateBgGainLevel();
        this.notifyListeners();
      };
      utterance.onerror = () => {
        this.isNarrationSpeaking = false;
        this.updateBgGainLevel();
        this.notifyListeners();
      };
      window.speechSynthesis.speak(utterance);
    } catch {
      this.isNarrationSpeaking = false;
      this.notifyListeners();
    }
  }

  public narrateQuizQuestion(params: {
    questionText: string;
    instructionText?: string;
    options?: string[];
    language?: string;
    force?: boolean;
  }) {
    if (!this.narrationEnabled && !params.force) return;
    this.speakQuestion(
      params.questionText,
      params.options || [],
      params.language || 'en',
      params.instructionText,
      params.force
    );
  }

  public speakQuestion(
    questionText: string,
    options: string[],
    langCode: string,
    format?: string,
    force?: boolean
  ) {
    if ((!this.narrationEnabled && !force) || typeof window === 'undefined') return;
    if (!('speechSynthesis' in window)) return;

    try {
      this.stopSpeaking();
      let fullScript = questionText;
      if (this.narrateInstructions && format) {
        fullScript = `${format}. ${fullScript}`;
      }
      if (this.narrateChoices && options && options.length > 0) {
        const labels = ['A', 'B', 'C', 'D'];
        const choicesScript = options
          .map((opt, idx) => `${labels[idx] || idx + 1}: ${opt}`)
          .join('. ');
        fullScript = `${fullScript}. Choices: ${choicesScript}`;
      }

      const utterance = new SpeechSynthesisUtterance(fullScript);
      const langMap: Record<string, string> = {
        en: 'en-US',
        tl: 'fil-PH',
        ceb: 'fil-PH',
        ko: 'ko-KR',
        'zh-CN': 'zh-CN',
        'zh-TW': 'zh-TW',
        es: 'es-ES',
      };
      utterance.lang = langMap[langCode] || 'en-US';
      utterance.rate = this.narrationRate;

      utterance.onstart = () => {
        this.isNarrationSpeaking = true;
        this.updateBgGainLevel();
        this.notifyListeners();
      };
      utterance.onend = () => {
        this.isNarrationSpeaking = false;
        this.updateBgGainLevel();
        this.notifyListeners();
      };
      utterance.onerror = () => {
        this.isNarrationSpeaking = false;
        this.updateBgGainLevel();
        this.notifyListeners();
      };

      window.speechSynthesis.speak(utterance);
    } catch {
      this.isNarrationSpeaking = false;
      this.notifyListeners();
    }
  }

  public stopSpeaking() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        // Ignore
      }
    }
    this.isNarrationSpeaking = false;
    this.updateBgGainLevel();
    this.notifyListeners();
  }
}

export const soundEngine = new SoundAndSpeechEngine();
